import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { WithdrawFormCreateDto } from '../../../../../../dto/withdrawals/baseWithdrawalInputDto/withdraw-create-form.dto';
import { useTranslation } from '../../../../../../hooks';
import ConfirmationMethodSelect from '../../../../../Confirmation/MethodSelect';

/**
 * TransactionsModalsWithdrawContentConfirmation Component
 * This component is responsible for rendering a confirmation method selection interface
 * during the withdrawal process. It utilizes translation for labels and manages form state
 * using React Hook Form.
 *
 * @returns {JSX.Element} The rendered component for selecting a confirmation method.
 *
 * @example
 * // Usage example:
 * <TransactionsModalsWithdrawContentConfirmation />
 */
export default function TransactionsModalsWithdrawContentConfirmation() {
  // Retrieve the translation function for the 'common' namespace
  const { t } = useTranslation('common');
  // Retrieve form context methods for watching and setting form values
  const { watch, setValue } = useFormContext<WithdrawFormCreateDto>();
  // Watch the 'emailId' field in the form
  const current: bigint | undefined = watch('crypto.emailId');

  /**
   * handleSelectEmail Function
   * This function updates the selected email ID in the form.
   *
   * @param {bigint} emailId - The email ID selected by the user.
   * @returns {void}
   *
   * @example
   * handleSelectEmail(12345678901234567890n);
   */
  const handleSelectEmail = useCallback(
    (emailId: bigint) => {
      setValue('crypto.emailId', emailId as never, { shouldDirty: true });
    },
    [setValue],
  );

  // Render the ConfirmationMethodSelect component with the necessary props
  return (
    <ConfirmationMethodSelect
      currentId={current as bigint}
      title={t('choose_confirmation_method')}
      onEmailSelect={handleSelectEmail}
    />
  );
}
